import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin, Stethoscope, Flame, Phone, Mail, Globe, PawPrint, ChevronDown, Heart, Facebook, Instagram } from "lucide-react";
import { HERO, MEMORIAL, FEATURE_CARDS, RESCUES, FOUNDER } from "../mock/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import PetDetailModal from "../components/PetDetailModal";
import BreedScanner from "../components/BreedScanner";
import FeatureModals from "../components/FeatureModals";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "dog", label: "Dogs" },
  { key: "cat", label: "Cats" },
  { key: "other", label: "Other" },
  { key: "medical", label: "❗ Needs vet / medical assistance" },
];

const colorMap = {
  orange: "text-[var(--paw-orange)]",
  green: "text-[var(--paw-sage)]",
  navy: "text-[#1B2A4E]",
};

export default function Home() {
  const navigate = useNavigate();
  const memorialRef = useRef(null);
  const [filter, setFilter] = useState("all");
  const [pet, setPet] = useState(null);
  const [petOpen, setPetOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [candles, setCandles] = useState(0);
  const [loves, setLoves] = useState(134);
  const [loved, setLoved] = useState(false);
  const addLove = async () => {
    if (loved) return;
    setLoved(true);
    setLoves((n) => n + 1);
    try {
      const { data } = await axios.post(`${API}/loves/lucky-memorial`, { delta: 1 });
      if (typeof data.count === "number") setLoves(data.count);
    } catch (e) {
      setLoved(false);
      setLoves((n) => n - 1);
    }
  };
  const payCandle = async () => {
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        kind: "candle",
        ref_id: "m-lucky",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast("Could not start checkout. Please try again.");
    }
  };
  const [showServices, setShowServices] = useState(false);
  const [rescues, setRescues] = useState(RESCUES);
  const [stats, setStats] = useState([
    { value: 5, label: "Looking for\na home", color: "orange" },
    { value: 1, label: "Safely in\nfoster care", color: "green" },
    { value: 6, label: "Total\nrescues", color: "navy" },
  ]);

  useEffect(() => {
    axios
      .get(`${API}/rescues`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setRescues(res.data);
      })
      .catch(() => {});
    axios
      .get(`${API}/stats`)
      .then((res) => {
        const s = res.data;
        setStats([
          { value: s.looking, label: "Looking for\na home", color: "orange" },
          { value: s.foster, label: "Safely in\nfoster care", color: "green" },
          { value: s.total, label: "Total\nrescues", color: "navy" },
        ]);
      })
      .catch(() => {});
    axios
      .get(`${API}/loves/lucky-memorial`)
      .then((res) => {
        if (typeof res.data?.count === "number") setLoves(res.data.count);
      })
      .catch(() => {});
    const lovesPoll = setInterval(() => {
      axios
        .get(`${API}/loves/lucky-memorial`)
        .then((res) => {
          if (typeof res.data?.count === "number") setLoves((n) => Math.max(n, res.data.count));
        })
        .catch(() => {});
    }, 20000);
    return () => clearInterval(lovesPoll);
  }, []);

  const urgent = rescues.find((r) => r.urgent);
  const grid = rescues.filter((r) => !r.urgent);
  const filtered =
    filter === "all"
      ? grid
      : filter === "medical"
      ? grid.filter((r) => r.vet)
      : grid.filter((r) => r.species === filter);

  const openPet = (p) => {
    setPet(p);
    setPetOpen(true);
  };

  const handleFeature = (action) => {
    if (action.startsWith("navigate:")) navigate(action.split(":")[1]);
    else if (action === "modal:scanner") setScannerOpen(true);
    else if (action.startsWith("modal:")) setModalType(action.split(":")[1]);
    else if (action === "scroll:memorial")
      memorialRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="pb-4">
      {/* Hero */}
      <div className="rounded-3xl overflow-hidden shadow-xl">
        <img loading="eager" decoding="async" fetchpriority="high" src={HERO.image} alt="Lucky" width="940" height="288" className="w-full h-72 object-cover object-[center_38%]" />
        <div className="bg-[var(--paw-navy-2)] p-5">
          <span className="inline-block bg-amber-400/20 text-amber-300 text-[11px] font-extrabold px-3 py-1 rounded-full tracking-wide">
            🐾 {HERO.badge}
          </span>
          <h1 className="font-head text-white text-3xl font-extrabold mt-3 leading-tight">
            {HERO.title}
          </h1>
          <p className="text-slate-300 font-semibold mt-1">
            {HERO.subtitle} <span className="text-red-400">❤️</span>
          </p>
          <button
            onClick={() => setShowServices((v) => !v)}
            className="mt-3 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-full px-3 py-1.5 transition-colors"
          >
            <PawPrint className="w-3.5 h-3.5 text-[var(--paw-orange)]" fill="currentColor" />
            <span className="text-[11px] font-extrabold text-white tracking-wide">
              Australia's all-in-one pet services platform
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-[var(--paw-orange)] transition-transform ${showServices ? "rotate-180" : ""}`}
            />
          </button>
          {showServices && (
            <div className="mt-2.5 flex flex-wrap gap-1.5 fade-up">
              {[
                "AI Breed Scanner",
                "Walk for Rewards",
                "Report a Stray",
                "Live Rescue Map",
                "Rescue",
                "Adopt",
                "Foster",
                "Rehome / Surrender",
                "Lost & Found Pets",
                "Spot & Snap a Stray",
                "Post Your Pet",
                "Dog Sitters",
                "Dog Groomers / Washes near you",
                "Foster Carers",
                "Dog Parks & Hot Spots",
                "Vets & Emergency ER",
                "Council Pounds",
                "Diets & Recipes",
                "Pawws Rewards",
                "Memorials",
                "Fundraisers & Donate",
                "Volunteer",
                "Partner With Us",
                "& more",
              ].map((s) => (
                <span
                  key={s}
                  className="text-[10px] font-bold text-white/90 bg-white/10 border border-white/10 rounded-full px-2 py-0.5"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* World-first pitch block */}
      <div className="mt-4 rounded-2xl p-4 bg-[#16264F] shadow-md">
        <span className="inline-flex items-center gap-1.5 bg-white/12 rounded-full px-2.5 py-1 max-w-full">
          <span className="text-[11px] shrink-0">🌎</span>
          <span className="text-[10px] font-extrabold text-[#E4A93A] whitespace-nowrap">
            First of its kind, ALL-IN-ONE pet services ecosystem
          </span>
        </span>
        <p className="text-white font-bold text-[13px] leading-snug mt-2">
          20+ services — including AI integrated features, product stores &amp; worldwide use. A
          real-world fix for a global problem.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        {stats.map((s) => (
          <div key={s.color} className="bg-white rounded-2xl py-4 px-2 text-center shadow-md">
            <div className={`font-head text-3xl font-extrabold ${colorMap[s.color]}`}>
              {s.value}
            </div>
            <div className="text-[11px] font-bold text-slate-500 leading-tight mt-1 whitespace-pre-line">
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Feature cards */}
      <div className="space-y-3 mt-4">
        {FEATURE_CARDS.map((c) => {
          const isDark = c.image || c.dark;
          const titleColor = isDark ? "text-white" : "text-[#1B2A4E]";
          const kickerColor = isDark ? "text-white/80" : "text-[#1B2A4E]/70";
          const subColor = isDark ? "text-white/85" : "text-[#1B2A4E]/70";
          return (
          <button
            key={c.id}
            onClick={() => handleFeature(c.action)}
            className="w-full text-left rounded-2xl p-4 shadow-md relative overflow-hidden flex items-center gap-3 active:scale-[0.99] transition-transform"
            style={{ background: c.image ? undefined : c.accent }}
          >
            {c.image && (
              <>
                <img loading="lazy" decoding="async" src={c.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-slate-900/55" />
              </>
            )}
            <div className="relative flex items-center gap-3 w-full">
              <div className={`w-11 h-11 rounded-2xl ${isDark ? "bg-white/25" : "bg-[#1B2A4E]/10"} backdrop-blur flex items-center justify-center text-xl shrink-0`}>
                {c.emoji}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-[10px] font-extrabold ${kickerColor} tracking-wide uppercase truncate`}>
                  {c.kicker}
                </div>
                <div className={`font-head font-extrabold ${titleColor} text-lg leading-tight`}>
                  {c.title}
                </div>
                <div className={`text-xs ${subColor} font-semibold leading-tight mt-0.5`}>
                  {c.subtitle}
                </div>
              </div>
              <div className={`w-9 h-9 rounded-full ${isDark ? "bg-white/90" : "bg-[#1B2A4E]"} flex items-center justify-center shrink-0 shadow-lg`}>
                <ArrowRight className={`w-4 h-4 ${isDark ? "text-[#1B2A4E]" : "text-white"}`} />
              </div>
            </div>
          </button>
          );
        })}
      </div>

      {/* Featured / urgent card */}
      {urgent && (
        <button
          onClick={() => openPet(urgent)}
          className="w-full text-left mt-5 rounded-2xl overflow-hidden shadow-lg relative"
          style={{ height: 200 }}
        >
          <img loading="lazy" decoding="async" src={urgent.image} alt={urgent.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/35" />
          <div className="absolute inset-0 flex flex-col justify-end p-4">
            <span className="self-start bg-[var(--paw-danger)] text-white text-[10px] font-extrabold px-2.5 py-1 rounded-full flex items-center gap-1 mb-2">
              <Flame className="w-3 h-3" /> URGENT • PICK UP TODAY
            </span>
            <div className="font-head text-white text-2xl font-extrabold leading-tight">{urgent.name}</div>
            <div className="text-sm text-white/85 font-semibold">{urgent.breed} • {urgent.suburb}</div>
          </div>
        </button>
      )}

      {/* Rescues grid */}
      <div className="mt-6" id="rescues-section" style={{ scrollMarginTop: "90px" }}>
        <h2 className="font-head text-[#1B2A4E] text-2xl font-extrabold">Meet our rescues</h2>
        <p className="text-[#1B2A4E]/60 text-sm font-semibold">Tap any pet to learn more</p>

        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-extrabold whitespace-nowrap transition-colors ${
                filter === f.key
                  ? "bg-[#1B2A4E] text-white"
                  : "bg-white text-[#1B2A4E] shadow-sm"
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => toast("Advanced filters — size, energy, kid-friendly & more 🔍")}
            className="px-4 py-1.5 rounded-full text-sm font-extrabold whitespace-nowrap bg-white text-[#1B2A4E] shadow-sm"
          >
            More
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => openPet(p)}
              className="bg-white rounded-2xl overflow-hidden shadow-md text-left active:scale-[0.98] transition-transform"
            >
              <div className="relative">
                <img loading="lazy" decoding="async" src={p.image} alt={p.name} className="w-full h-32 object-cover" />
                <span className="absolute top-2 left-2 bg-white/90 text-slate-900 text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                  {p.speciesEmoji} {p.species}
                </span>
                {p.vet && (
                  <span className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <Stethoscope className="w-2.5 h-2.5" /> VET
                  </span>
                )}
                <span
                  className={`absolute bottom-2 left-2 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                    p.status === "fostered" ? "bg-emerald-500" : "bg-[var(--paw-orange)]"
                  }`}
                >
                  {p.status === "fostered" ? "Fostered" : "Needs home"}
                </span>
              </div>
              <div className="p-2.5">
                <div className="font-head font-extrabold text-slate-900 leading-tight">
                  {p.name}
                </div>
                <div className="text-[11px] text-slate-500 font-bold flex items-center gap-0.5">
                  <MapPin className="w-3 h-3" /> {p.suburb}
                </div>
                <div className="text-[11px] text-slate-400 font-semibold">{p.breed}</div>
                <div className="mt-1.5 text-[11px] font-extrabold text-emerald-600">{p.fee}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Memorial */}
      <div
        ref={memorialRef}
        data-testid="home-memorial-card"
        className="mt-8 mx-0 rounded-[22px] overflow-hidden bg-[#16264F] shadow-xl"
      >
        <div className="p-4 pb-3">
          <div className="text-xs font-extrabold text-[#E4A93A] tracking-wide">
            In memory of a major inspiration to pawwprints 🙌
          </div>
        </div>
        <div className="mx-4 mb-3 rounded-[20px] border-[4px] border-[#E4A93A] overflow-hidden bg-[#16264F]">
          <img
            src={MEMORIAL.image}
            alt={MEMORIAL.name}
            width="1080"
            height="2028"
            loading="lazy"
            decoding="async"
            className="block w-full h-auto"
          />
        </div>
        <div className="p-4">
          <div className="font-head text-white text-2xl font-extrabold">{MEMORIAL.name} 🐺</div>
          <div className="text-xs italic text-white/70 mt-0.5">posted by {MEMORIAL.posted_by}</div>
          <p className="text-sm text-white/90 mt-2 leading-relaxed">{MEMORIAL.text}</p>

          <div className="flex items-center justify-between mt-4 gap-2.5">
            <button
              data-testid="home-love-btn"
              onClick={addLove}
              className="flex items-center gap-1.5 active:scale-95 transition-transform"
            >
              <span className="font-bold text-sm text-white">{loves} loves</span>
              <Heart size={28} className="text-[#D76A6A]" fill={loved ? "#D76A6A" : "none"} />
            </button>
            <button
              data-testid="home-light-candle-btn"
              onClick={payCandle}
              className="flex items-center justify-center gap-1.5 py-3 px-[18px] rounded-full border-[1.5px] border-[#E4A93A] bg-white/[0.06] active:scale-95 transition-transform"
            >
              <span className="text-[15px] candle-flame">🕯️</span>
              <span className="font-bold text-sm text-[#E4A93A]">Light a candle $5</span>
            </button>
          </div>
        </div>
      </div>

      {/* Founder */}
      <div className="mt-6 bg-white rounded-3xl p-5 shadow-md">
        <div className="text-[11px] font-extrabold text-[var(--paw-orange)] tracking-wide">FOUNDER</div>
        <div className="font-head text-2xl font-extrabold text-slate-900 mt-1">{FOUNDER.name}</div>
        <div className="mt-2 space-y-0.5">
          {FOUNDER.roles.map((r) => (
            <div key={r} className="text-sm text-slate-500 font-semibold">{r}</div>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          <a href={`https://${FOUNDER.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-700 font-bold text-sm">
            <Globe className="w-4 h-4 text-[var(--paw-orange)]" /> {FOUNDER.website}
          </a>
          <a href="tel:0400009372" className="flex items-center gap-2 text-slate-700 font-bold text-sm">
            <Phone className="w-4 h-4 text-[var(--paw-orange)]" /> {FOUNDER.phone}
          </a>
          {FOUNDER.emails.map((em) => (
            <a key={em} href={`mailto:${em}`} className="flex items-center gap-2 text-slate-700 font-bold text-sm">
              <Mail className="w-4 h-4 text-[var(--paw-orange)]" /> {em}
            </a>
          ))}
        </div>
      </div>

      {/* Donate to the cause CTA */}
      <button
        data-testid="home-donate-cta"
        onClick={() => navigate("/donate/general")}
        className="w-full mt-8 rounded-3xl p-5 bg-gradient-to-br from-[#16264F] to-[#274a86] shadow-xl text-center active:scale-[0.99] transition-transform relative overflow-hidden"
      >
        <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-2">
          <Heart className="w-7 h-7 text-[#E07A5F]" fill="#E07A5F" />
        </div>
        <div className="font-head text-white text-2xl font-extrabold">Donate to our cause</div>
        <div className="text-[13px] text-white/75 font-semibold mt-1">
          100% of proceeds go to animal shelters &amp; rescue services 💙
        </div>
        <span className="inline-block mt-3 bg-[#E4A93A] text-[#16264F] text-sm font-extrabold px-6 py-2.5 rounded-full">
          Give today
        </span>
      </button>

      <div className="flex items-center justify-center gap-3 mt-8">
        <a
          href="https://www.facebook.com/share/14jVbgrRTzm/"
          target="_blank"
          rel="noreferrer"
          data-testid="social-facebook"
          aria-label="Pawwprints on Facebook"
          className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center active:scale-95 transition-transform"
        >
          <Facebook className="w-6 h-6 text-[#1877F2]" fill="#1877F2" stroke="#1877F2" />
        </a>
        <a
          href="https://www.instagram.com/pawwprintsau?igsh=eGF3N2JpOW0wMjNr"
          target="_blank"
          rel="noreferrer"
          data-testid="social-instagram"
          aria-label="Pawwprints on Instagram"
          className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center active:scale-95 transition-transform"
        >
          <Instagram className="w-6 h-6 text-[#E1306C]" />
        </a>
        <a
          href="https://wa.me/61400009372"
          target="_blank"
          rel="noreferrer"
          data-testid="social-whatsapp"
          aria-label="Pawwprints on WhatsApp"
          className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center active:scale-95 transition-transform"
        >
          <svg viewBox="0 0 24 24" fill="#25D366" className="w-6 h-6" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
          </svg>
        </a>
      </div>

      <p className="text-center text-slate-400 text-sm font-semibold mt-6">
        Built with <span className="text-red-400">❤️</span> in Melbourne, Australia
      </p>

      <PetDetailModal pet={pet} open={petOpen} onOpenChange={setPetOpen} />
      <BreedScanner open={scannerOpen} onOpenChange={setScannerOpen} />
      <FeatureModals type={modalType} onClose={() => setModalType(null)} />
    </div>
  );
}
